<script lang="ts">
  import { Laboratorio, Consultorios, type ExamenCatalogo, type OrdenLab, type OrdenLabDetalle, type Consultorio, type Profesional } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import { session } from '../lib/auth.svelte';
  import { estadoLabLabel, estadoLabHint } from '../lib/estudio-estados';

  const LAB_PAQUETES_KEY = 'toscana.lab.paquetes.v1';
  type PaqueteLab = { nombre: string; codigos: string[] };

  function loadPaquetesLab(): PaqueteLab[] {
    try {
      const raw = localStorage.getItem(LAB_PAQUETES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }

  let catalogo = $state<ExamenCatalogo[]>([]);
  let ordenes = $state<OrdenLab[]>([]);
  let detalle = $state<OrdenLabDetalle | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let busquedaExamen = $state('');
  let paquetesLab = $state<PaqueteLab[]>(typeof localStorage !== 'undefined' ? loadPaquetesLab() : []);

  // Atribución (consultorios / clínicas asociadas)
  let consultorios = $state<Consultorio[]>([]);
  let profesionales = $state<Profesional[]>([]);

  // Nueva orden
  let nueva = $state<{ pacienteId: string; pacienteNombre: string; medico: string; prioridad: 'RUTINA' | 'URGENTE'; consultorioId: string; medicoId: string; pagador: 'PACIENTE' | 'CONSULTORIO' | 'ASEGURADORA'; seleccion: Record<string, boolean> }>(
    { pacienteId: '', pacienteNombre: '', medico: '', prioridad: 'RUTINA', consultorioId: '', medicoId: '', pagador: 'PACIENTE', seleccion: {} }
  );

  $effect(() => {
    Consultorios.list().then(c => consultorios = c).catch(() => {});
    Consultorios.profesionales().then(p => profesionales = p).catch(() => {});
  });

  // Captura de resultados (por renglón)
  let valores = $state<Record<string, string>>({});

  // Notificación
  let notif = $state<{ email: string; telefono: string; email_on: boolean; wa_on: boolean }>(
    { email: '', telefono: '', email_on: true, wa_on: true }
  );

  // Nuevo examen del catálogo
  let nuevoEx = $state<ExamenCatalogo>({ codigo: '', nombre: '', categoria: 'QUIMICA_SANGUINEA', unidad: '', valorRefMin: null, valorRefMax: null, precio: 0 });
  let mostrarCatalogo = $state(true);
  let editingCodigo = $state<string | null>(null);
  let editPrecio = $state<number | string>(0);

  const CATEGORIAS = ['HEMATOLOGIA', 'QUIMICA_SANGUINEA', 'ORINA', 'HECES', 'INMUNOLOGIA', 'MICROBIOLOGIA', 'HORMONAS', 'COAGULACION', 'OTROS'];

  const seleccionados = $derived(Object.keys(nueva.seleccion).filter((k) => nueva.seleccion[k]));
  const totalEstimado = $derived(
    catalogo.filter((e) => e.codigo && nueva.seleccion[e.codigo]).reduce((s, e) => s + (e.precio ?? 0), 0)
  );
  const catalogoFiltrado = $derived(
    !busquedaExamen.trim() ? catalogo :
    catalogo.filter(e => {
      const q = busquedaExamen.toLowerCase();
      return (e.nombre ?? '').toLowerCase().includes(q) || (e.codigo ?? '').toLowerCase().includes(q) || (e.categoria ?? '').toLowerCase().includes(q);
    })
  );
  const puedeAgregarRapido = $derived(
    busquedaExamen.trim().length >= 2 &&
    !catalogo.some(e => (e.nombre ?? '').toLowerCase() === busquedaExamen.trim().toLowerCase())
  );

  function slugCodigo(nombre: string, pref: string): string {
    const base = nombre.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toUpperCase().slice(0, 18);
    return `${pref}-${base || 'N'}-${Date.now().toString(36).slice(-4).toUpperCase()}`;
  }

  async function agregarExamenRapido() {
    const nombre = busquedaExamen.trim();
    if (!nombre) return;
    error = null;
    try {
      const codigo = slugCodigo(nombre, 'LAB');
      const created = await Laboratorio.crearExamen({ codigo, nombre, categoria: 'OTROS', precio: 0 });
      catalogo = await Laboratorio.catalogo();
      const code = created?.codigo ?? codigo;
      nueva.seleccion = { ...nueva.seleccion, [code]: true };
      busquedaExamen = '';
      info = `«${nombre}» agregado al catálogo y seleccionado.`;
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  function guardarPaqueteLab() {
    const nombre = prompt('Nombre del paquete:');
    if (!nombre?.trim()) return;
    if (seleccionados.length === 0) { error = 'Selecciona al menos un examen.'; return; }
    const next = [...paquetesLab.filter(p => p.nombre !== nombre.trim()), { nombre: nombre.trim(), codigos: [...seleccionados] }];
    paquetesLab = next;
    localStorage.setItem(LAB_PAQUETES_KEY, JSON.stringify(next));
    info = `Paquete «${nombre.trim()}» guardado.`;
  }

  function aplicarPaqueteLab(p: PaqueteLab) {
    const sel = { ...nueva.seleccion };
    for (const c of p.codigos) sel[c] = true;
    nueva.seleccion = sel;
  }

  async function cargar() {
    loading = true; error = null;
    try {
      [catalogo, ordenes] = await Promise.all([Laboratorio.catalogo(), Laboratorio.ordenes()]);
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function abrir(id: string) {
    error = null; info = null;
    try { detalle = await Laboratorio.orden(id); valores = {}; }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function crearOrden(e: SubmitEvent) {
    e.preventDefault();
    error = null; info = null;
    if (!nueva.pacienteId) { error = 'Selecciona un paciente.'; return; }
    if (seleccionados.length === 0) { error = 'Selecciona al menos un examen.'; return; }
    try {
      const d = await Laboratorio.crearOrden({
        pacienteId: nueva.pacienteId,
        pacienteNombre: nueva.pacienteNombre || undefined,
        medicoSolicitante: nueva.medico || undefined,
        consultorioId: nueva.consultorioId || undefined,
        medicoSolicitanteId: nueva.medicoId || undefined,
        pagador: nueva.pagador,
        prioridad: nueva.prioridad,
        examenes: seleccionados,
      });
      detalle = d;
      info = `Orden ${d.orden.numero} creada.`;
      nueva = { pacienteId: '', pacienteNombre: '', medico: '', prioridad: 'RUTINA', consultorioId: '', medicoId: '', pagador: 'PACIENTE', seleccion: {} };
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function guardarResultado(renglonId: string) {
    const valor = (valores[renglonId] ?? '').trim();
    if (!valor || !detalle) return;
    error = null;
    try {
      detalle = await Laboratorio.resultado(detalle.orden.id!, renglonId, { valor, resultadoPor: session.value?.usuario ?? 'laboratorio' });
      info = 'Resultado guardado.';
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function validar() {
    if (!detalle) return;
    error = null;
    try {
      detalle = await Laboratorio.validar(detalle.orden.id!, session.value?.usuario ?? 'Profesional de laboratorio');
      info = `Orden validada. Folio ${detalle.orden.codigoValidacion}.`;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function entregar() {
    if (!detalle) return;
    try { detalle = await Laboratorio.entregar(detalle.orden.id!); info = 'Orden entregada.'; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function verInforme() {
    if (!detalle) return;
    try {
      const html = await Laboratorio.reporte(detalle.orden.id!);
      const w = window.open('', '_blank');
      if (w) { w.document.open(); w.document.write(html); w.document.close(); }
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function enviar() {
    if (!detalle) return;
    error = null; info = null;
    const canales: string[] = [];
    if (notif.email_on) canales.push('EMAIL');
    if (notif.wa_on) canales.push('WHATSAPP');
    if (canales.length === 0) { error = 'Selecciona al menos un canal.'; return; }
    try {
      const r = await Laboratorio.notificar(detalle.orden.id!, { canales, email: notif.email, telefono: notif.telefono });
      const estados = r.map((n: any) => `${n.canal}:${n.estado}`).join(', ');
      info = `Notificaciones: ${estados}`;
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function cargarBase() {
    error = null; info = null;
    try {
      const r = await Laboratorio.seed();
      info = `Catálogo base: ${r.insertados} agregados, ${r.existentes} ya existían.`;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function crearExamen(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    try {
      await Laboratorio.crearExamen({
        ...nuevoEx,
        valorRefMin: nuevoEx.valorRefMin != null && (nuevoEx.valorRefMin as any) !== '' ? Number(nuevoEx.valorRefMin) : null,
        valorRefMax: nuevoEx.valorRefMax != null && (nuevoEx.valorRefMax as any) !== '' ? Number(nuevoEx.valorRefMax) : null,
        precio: Number(nuevoEx.precio ?? 0),
      });
      nuevoEx = { codigo: '', nombre: '', categoria: 'QUIMICA_SANGUINEA', unidad: '', valorRefMin: null, valorRefMax: null, precio: 0 };
      await cargar();
      info = 'Examen agregado al catálogo.';
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function guardarPrecio(e: ExamenCatalogo) {
    error = null;
    try {
      await Laboratorio.actualizarExamen(e.codigo, { ...e, precio: Number(editPrecio) });
      editingCodigo = null;
      info = `Precio de ${e.nombre} actualizado.`;
      await cargar();
    } catch (err: any) { error = err?.message ?? String(err); }
  }

  function flagClass(f?: string | null) {
    if (f === 'ALTO') return 'f-alto';
    if (f === 'BAJO') return 'f-bajo';
    if (f === 'CRITICO') return 'f-crit';
    return '';
  }
  function refTexto(e: { valorRefTexto?: string | null; valorRefMin?: number | null; valorRefMax?: number | null }) {
    if (e.valorRefTexto) return e.valorRefTexto;
    if (e.valorRefMin != null && e.valorRefMax != null) return `${e.valorRefMin} - ${e.valorRefMax}`;
    if (e.valorRefMin != null) return `≥ ${e.valorRefMin}`;
    if (e.valorRefMax != null) return `≤ ${e.valorRefMax}`;
    return '—';
  }

  $effect(() => { cargar(); });
</script>

<section>
  <header class="view-header">
    <div><h2>Laboratorio clínico</h2><p class="muted">Órdenes, captura de resultados e informes</p></div>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <div class="cols">
    <!-- Columna izquierda: nueva orden + lista -->
    <div>
      <div class="panel">
        <h3>Nueva orden</h3>
        <form onsubmit={crearOrden} class="stack">
          <label class="field"><span>Paciente *</span>
            <PacientePicker bind:value={nueva.pacienteId} onPick={(id, p) => { nueva.pacienteId = id; nueva.pacienteNombre = p ? `${p.nombres} ${p.apellidos}` : ''; }} placeholder="Buscar paciente…" />
          </label>
          <div class="row">
            <label class="field grow"><span>Médico solicitante</span>
              <input placeholder="Nombre del médico" bind:value={nueva.medico} />
            </label>
            <label class="field"><span>Prioridad</span>
              <select bind:value={nueva.prioridad}>
                <option value="RUTINA">Rutina</option>
                <option value="URGENTE">Urgente</option>
              </select>
            </label>
          </div>
          <div class="row">
            <label class="field grow"><span>Consultorio (opcional)</span>
              <select bind:value={nueva.consultorioId}>
                <option value="">— Consultorio (opcional) —</option>
                {#each consultorios as c (c.id)}<option value={c.id}>{c.nombre}</option>{/each}
              </select>
            </label>
            <label class="field grow"><span>Profesional (opcional)</span>
              <select bind:value={nueva.medicoId}>
                <option value="">— Profesional (opcional) —</option>
                {#each profesionales as p (p.id)}<option value={p.id}>{p.nombre}</option>{/each}
              </select>
            </label>
            <label class="field"><span>Pagador</span>
              <select bind:value={nueva.pagador}>
                <option value="PACIENTE">Paciente</option>
                <option value="CONSULTORIO">Consultorio</option>
                <option value="ASEGURADORA">Aseguradora</option>
              </select>
            </label>
          </div>

          <div class="filter-bar">
            <input
              type="search"
              placeholder="Buscar examen por nombre o código…"
              bind:value={busquedaExamen}
            />
          </div>
          {#if puedeAgregarRapido}
            <div class="quick-add">
              <span>¿No aparece en la lista?</span>
              <button type="button" class="ghost sm" onclick={agregarExamenRapido}>
                Agregar «{busquedaExamen.trim()}» y seleccionar
              </button>
            </div>
          {/if}
          <div class="exams-scroll">
            <table class="exams-table">
              <thead><tr><th></th><th>Examen</th><th>Categoría</th><th class="num">Precio</th><th></th></tr></thead>
              <tbody>
                {#each catalogoFiltrado as e (e.id)}
                  <tr class:on={nueva.seleccion[e.codigo]}>
                    <td class="chk"><input type="checkbox" bind:checked={nueva.seleccion[e.codigo]} /></td>
                    <td>{e.nombre}</td>
                    <td>{(e.categoria ?? '').replace('_', ' ')}</td>
                    <td class="num">
                      {#if editingCodigo === e.codigo}
                        <input type="number" step="0.01" bind:value={editPrecio} style="width:5.5rem" />
                      {:else}
                        Q{(e.precio ?? 0).toFixed(2)}
                      {/if}
                    </td>
                    <td>
                      {#if editingCodigo === e.codigo}
                        <button type="button" class="ghost sm" onclick={() => guardarPrecio(e)}>Guardar</button>
                      {:else}
                        <button type="button" class="link" onclick={() => { editingCodigo = e.codigo; editPrecio = e.precio ?? 0; }}>Editar</button>
                      {/if}
                    </td>
                  </tr>
                {:else}
                  <tr><td colspan="5" class="empty">Sin exámenes en el catálogo. Usa "Cargar catálogo base" abajo.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>

          <div class="paquetes">
            <button type="button" class="ghost sm" onclick={guardarPaqueteLab}>Guardar selección como paquete</button>
            {#each paquetesLab as p (p.nombre)}
              <button type="button" class="chip" onclick={() => aplicarPaqueteLab(p)}>{p.nombre}</button>
            {/each}
          </div>

          <div class="row between">
            <span class="total">{seleccionados.length} examen(es) · Total Q{totalEstimado.toFixed(2)}</span>
            <button type="submit">Crear orden</button>
          </div>
        </form>
      </div>

      <div class="panel">
        <h3>Órdenes recientes</h3>
        <table>
          <thead><tr><th>N°</th><th>Paciente</th><th>Estado</th></tr></thead>
          <tbody>
            {#each ordenes as o (o.id)}
              <tr class:sel={detalle?.orden.id === o.id} onclick={() => abrir(o.id!)}>
                <td>{o.numero}</td>
                <td>{o.pacienteNombre}</td>
                <td><span class="badge b-{o.estado}" title={estadoLabHint(o.estado)}>{estadoLabLabel(o.estado)}</span></td>
              </tr>
            {:else}<tr><td colspan="3" class="empty">Sin órdenes.</td></tr>{/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Columna derecha: detalle -->
    <div>
      {#if detalle}
        {@const o = detalle.orden}
        <div class="panel">
          <h3>Orden {o.numero} <span class="badge b-{o.estado}" title={estadoLabHint(o.estado)}>{estadoLabLabel(o.estado)}</span></h3>
          <p class="meta">{o.pacienteNombre}{o.medicoSolicitante ? ` · ${o.medicoSolicitante}` : ''} · Total Q{(o.total ?? 0).toFixed(2)}</p>

          <table class="res">
            <thead><tr><th>Examen</th><th>Resultado</th><th>Ref.</th></tr></thead>
            <tbody>
              {#each detalle.examenes as ex (ex.id)}
                <tr>
                  <td>{ex.examenNombre}<br><small class="cat">{(ex.categoria ?? '').replace('_', ' ')}</small></td>
                  <td>
                    {#if ex.valor}
                      <span class="val {flagClass(ex.flag)}">{ex.valor} {ex.unidad ?? ''}</span>
                      {#if ex.flag && ex.flag !== 'NORMAL' && ex.flag !== 'SIN_RANGO'}<span class="fbadge {flagClass(ex.flag)}">{ex.flag}</span>{/if}
                    {:else if o.estado === 'SOLICITADA' || o.estado === 'EN_PROCESO'}
                      <span class="capture">
                        <input placeholder="valor" bind:value={valores[ex.id!]} />
                        <button type="button" onclick={() => guardarResultado(ex.id!)}>✓</button>
                      </span>
                    {:else}—{/if}
                  </td>
                  <td><small>{refTexto(ex)} {ex.unidad ?? ''}</small></td>
                </tr>
              {/each}
            </tbody>
          </table>

          <div class="acciones">
            {#if o.estado === 'RESULTADOS_LISTOS'}
              <button onclick={validar}>Validar (firmar)</button>
            {/if}
            {#if o.codigoValidacion}
              <button class="ghost" onclick={verInforme}>Ver informe</button>
              {#if o.estado === 'VALIDADA'}<button class="ghost" onclick={entregar}>Marcar entregada</button>{/if}
            {/if}
          </div>

          {#if o.codigoValidacion}
            <div class="folio">
              Folio de verificación: <strong>{o.codigoValidacion}</strong> · Validado por {o.validadaPor}
              {#if o.cuentaId}<br><span class="cargo">Cargado a la cuenta del paciente (Q{(o.total ?? 0).toFixed(2)})</span>{/if}
            </div>
          {/if}
        </div>

        {#if o.codigoValidacion}
          <div class="panel">
            <h3>Enviar resultados</h3>
            <div class="stack">
              <div class="row">
                <label class="chk"><input type="checkbox" bind:checked={notif.email_on} /> Correo</label>
                <label class="field grow"><span>Email</span><input placeholder="correo@paciente.com" bind:value={notif.email} /></label>
              </div>
              <div class="row">
                <label class="chk"><input type="checkbox" bind:checked={notif.wa_on} /> WhatsApp</label>
                <label class="field grow"><span>Teléfono</span><input placeholder="+502 5555-5555" bind:value={notif.telefono} /></label>
              </div>
              <button onclick={enviar}>Enviar enlace al portal</button>
            </div>
          </div>
        {/if}
      {:else}
        <div class="panel">
          <p class="hint">Selecciona o crea una orden para ver el detalle, capturar resultados y enviar el informe.</p>
        </div>
      {/if}
    </div>
  </div>

  <!-- Catálogo (buscar, agregar y editar precios) -->
  <div class="panel">
    <h3 class="cat-head">
      <button class="link" onclick={() => mostrarCatalogo = !mostrarCatalogo}>{mostrarCatalogo ? '▾' : '▸'} Catálogo de exámenes ({catalogo.length})</button>
      <button type="button" class="ghost sm" onclick={cargarBase}>Cargar catálogo base</button>
    </h3>
    {#if mostrarCatalogo}
      <div class="filter-bar">
        <input
          type="search"
          placeholder="Buscar examen por nombre o código para agregar o editar precio…"
          bind:value={busquedaExamen}
        />
      </div>
      {#if puedeAgregarRapido}
        <div class="quick-add">
          <span>¿No aparece en el catálogo?</span>
          <button type="button" class="ghost sm" onclick={agregarExamenRapido}>
            Agregar «{busquedaExamen.trim()}» al catálogo
          </button>
        </div>
      {/if}
      <form onsubmit={crearExamen} class="form-grid">
        <label class="field"><span>Código</span><input placeholder="Código" bind:value={nuevoEx.codigo} required /></label>
        <label class="field"><span>Nombre</span><input placeholder="Nombre" bind:value={nuevoEx.nombre} required /></label>
        <label class="field"><span>Categoría</span>
          <select bind:value={nuevoEx.categoria}>
            {#each CATEGORIAS as c}<option value={c}>{c.replace('_', ' ')}</option>{/each}
          </select>
        </label>
        <label class="field"><span>Unidad</span><input placeholder="Unidad" bind:value={nuevoEx.unidad} /></label>
        <label class="field"><span>Ref. mín</span><input type="number" step="any" placeholder="0" bind:value={nuevoEx.valorRefMin} /></label>
        <label class="field"><span>Ref. máx</span><input type="number" step="any" placeholder="0" bind:value={nuevoEx.valorRefMax} /></label>
        <label class="field"><span>Precio (Q)</span><input type="number" step="any" placeholder="0.00" bind:value={nuevoEx.precio} /></label>
        <button type="submit">+ Examen</button>
      </form>
      <table>
        <thead><tr><th>Código</th><th>Nombre</th><th>Categoría</th><th>Ref.</th><th class="num">Precio</th><th>Acciones</th></tr></thead>
        <tbody>
          {#each catalogoFiltrado as e (e.id)}
            <tr>
              <td>{e.codigo}</td>
              <td>{e.nombre}</td>
              <td>{e.categoria.replace('_', ' ')}</td>
              <td>{refTexto(e)} {e.unidad ?? ''}</td>
              <td class="num">
                {#if editingCodigo === e.codigo}
                  <input type="number" step="any" class="edit-precio" bind:value={editPrecio} />
                {:else}
                  Q{(e.precio ?? 0).toFixed(2)}
                {/if}
              </td>
              <td>
                {#if editingCodigo === e.codigo}
                  <button type="button" class="ghost sm" onclick={() => guardarPrecio(e)}>Guardar</button>
                  <button type="button" class="link" onclick={() => editingCodigo = null}>Cancelar</button>
                {:else}
                  <button type="button" class="link" onclick={() => { editingCodigo = e.codigo; editPrecio = e.precio ?? 0; }}>Editar precio</button>
                {/if}
              </td>
            </tr>
          {:else}<tr><td colspan="6" class="empty">Catálogo vacío.</td></tr>{/each}
        </tbody>
      </table>
    {/if}
  </div>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  h3 { margin: 0 0 .5rem; font-size: 1rem; color: #166534; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin: 0 0 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; align-items: start; }
  @media (max-width: 860px) { .cols { grid-template-columns: 1fr; } }
  .stack { display: flex; flex-direction: column; gap: .6rem; }
  .row { display: flex; gap: .6rem; align-items: flex-end; flex-wrap: wrap; }
  .row.between { justify-content: space-between; align-items: center; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .grow { flex: 1; min-width: 140px; }
  input, select { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  input:focus, select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .exams-scroll { max-height: 360px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 8px; }
  .filter-bar { margin-bottom: .45rem; }
  .filter-bar input { width: 100%; box-sizing: border-box; }
  .quick-add { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; margin: .35rem 0 .65rem; padding: .5rem .65rem; background: #f0fdf4; border-radius: 8px; font-size: .88rem; }
  .paquetes { display: flex; flex-wrap: wrap; gap: .4rem; align-items: center; margin: .55rem 0 .25rem; }
  .chip { font-size: .78rem; padding: .28rem .55rem; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #166534; cursor: pointer; font-weight: 500; box-shadow: none; }
  .chip:hover { background: #f0fdf4; border-color: #16a34a; transform: none; }
  .exams-table { margin-top: 0; }
  .exams-table thead th { position: sticky; top: 0; z-index: 1; }
  .exams-table td { font-size: .85rem; }
  .exams-table td.chk { width: 2.2rem; text-align: center; }
  .exams-table input[type=checkbox] { accent-color: #16a34a; }
  tr.on { background: #f0fdf4; }
  .total { font-size: .85rem; color: #166534; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .45rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .88rem; color: #111827; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  tbody tr { cursor: pointer; }
  tbody tr.sel { background: #f0fdf4; }
  .res tbody tr, .exams-table tbody tr { cursor: default; }
  .meta { color: #6b7280; font-size: .85rem; margin: .2rem 0 .4rem; }
  .capture { display: flex; gap: .25rem; align-items: center; }
  .capture input { width: 90px; }
  .capture button { padding: .3rem .55rem; box-shadow: none; }
  .val { font-weight: 700; }
  .f-alto { color: #b91c1c; } .f-bajo { color: #1d4ed8; } .f-crit { color: #fff; background: #b91c1c; padding: 0 .3rem; border-radius: 4px; }
  .fbadge { font-size: .68rem; font-weight: 700; margin-left: .35rem; padding: .05rem .35rem; border-radius: 6px; border: 1px solid currentColor; }
  .cat { color: #9ca3af; text-transform: capitalize; }
  .acciones { display: flex; gap: .5rem; margin-top: .75rem; flex-wrap: wrap; }
  .ghost { background: #fff; color: #16a34a; border: 1px solid #16a34a; cursor: pointer; font-weight: 600; box-shadow: none; }
  .ghost:hover { background: #f0fdf4; }
  .folio { margin-top: .6rem; background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; padding: .5rem .65rem; font-size: .85rem; color: #166534; }
  .cargo { color: #15803d; font-weight: 600; }
  .chk { display: flex; align-items: center; gap: .3rem; font-size: .85rem; min-width: 95px; color: #374151; align-self: center; }
  .chk input { accent-color: #16a34a; }
  .form-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: .6rem; margin-bottom: .6rem; align-items: end; }
  .form-grid > button { align-self: end; }
  .badge { font-size: .68rem; padding: .1rem .45rem; border-radius: 999px; font-weight: 600; }
  .b-SOLICITADA { background: #f3f4f6; color: #374151; }
  .b-EN_PROCESO { background: #fef3c7; color: #92400e; }
  .b-RESULTADOS_LISTOS { background: #dcfce7; color: #166534; }
  .b-VALIDADA { background: #bbf7d0; color: #14532d; }
  .b-ENTREGADA { background: #ecfdf5; color: #15803d; }
  .b-ANULADA { background: #fee2e2; color: #dc2626; }
  .link { background: transparent; border: 0; color: #16a34a; cursor: pointer; font: inherit; padding: 0; box-shadow: none; }
  .link:hover { transform: none; }
  .empty, .hint { color: #9ca3af; font-style: italic; font-size: .85rem; }
  .cat-head { display: flex; align-items: center; gap: .75rem; margin: 0; }
  .ghost.sm { padding: .25rem .6rem; font-size: .8rem; border-radius: 8px; }
  .edit-precio { width: 5.5rem; padding: .3rem .4rem; }
  .err { color: #dc2626; }
  .ok { color: #166534; background: #dcfce7; padding: .35rem .55rem; border-radius: 8px; }
</style>
